import { tellUsOnceSpec } from '@/karehero-app/explore/tell-us-once/tell-us-once';
import { benefitsCalculatorSpec } from '@/karehero-app/explore/benefits-calculator/benefits-calculator';
import { paywallSpec } from '@/cam-admin/explore/paywall/paywall';
import { type ExplorationSpec } from '@/designs-dev/explore/types';

// The set of explorations, so the menu and any future board can enumerate
// them. An exploration is upstream of committed work by definition, so it never
// carries a build state. A frame graduates by becoming a prototype and then a
// handover — the verdict field records where it went.
export const EXPLORATIONS: ExplorationSpec[] = [
  tellUsOnceSpec,
  benefitsCalculatorSpec,
  paywallSpec,
];

// A project: several exploration pages that are one piece of work. One piece of
// work should be one thing in the Loft sidebar, so a project's pages share a
// route prefix and are switched between from a menu inside the page rather than
// from the rail. The label is a property of being a page in this project, not of
// the spec — the spec's own title is a sentence, and a menu wants a noun.
export type ExplorationProject = {
  // Route prefix: /explore/<id>.
  id: string;
  title: string;
  // In reading order. The first page is what /explore/<id> lands on, so it is
  // also the page the sidebar entry points at.
  pages: { label: string; spec: ExplorationSpec }[];
};

export const PROJECTS: ExplorationProject[] = [
  {
    id: 'tell-us-once',
    title: 'Tell us once',
    pages: [
      { label: 'Exploration', spec: tellUsOnceSpec },
      { label: 'Benefits calculator', spec: benefitsCalculatorSpec },
    ],
  },
  {
    id: 'blurred-profile-paywall',
    title: 'Blurred-profile paywall',
    pages: [{ label: 'The flow end to end', spec: paywallSpec }],
  },
];

// The project a page belongs to, if any. A standalone exploration has none and
// keeps its own /explore/<id> route.
export const projectForSpec = (
  spec: ExplorationSpec,
): ExplorationProject | undefined =>
  PROJECTS.find((project) => project.pages.some((page) => page.spec === spec));

// Where an exploration renders. Standalone: /explore/<spec id>. Inside a
// project: the first page sits at the project root so the sidebar has one entry,
// and the rest hang off it.
export const explorationRoute = (spec: ExplorationSpec): string => {
  const project = projectForSpec(spec);
  if (!project) return `/explore/${spec.id}`;
  return project.pages[0].spec === spec
    ? `/explore/${project.id}`
    : `/explore/${project.id}/${spec.id}`;
};
